<template>
  <div class="section-card review-health-card" v-if="visible">
    <div class="section-header">
      <div class="section-title">
        <div class="icon-wrap">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
        </div>
        Review Health
      </div>
    </div>

    <div v-if="waiting" class="review-health-waiting">
      Waiting for Amazon's first review data
    </div>

    <template v-else>
      <div class="health-metrics review-health-metrics">
        <div class="health-metric">
          <span class="health-metric-name">Star rating</span>
          <span class="health-metric-value" :class="{ critical: flagged('rating_drop') }">
            {{ ratingText }}
            <span v-if="previousRatingText" class="threshold">{{ previousRatingText }}</span>
          </span>
        </div>
        <div class="health-metric">
          <span class="health-metric-name">Rating count</span>
          <span class="health-metric-value" :class="{ critical: flagged('rating_count_drop') }">
            {{ countText }}
            <span v-if="countDeltaText" class="threshold">{{ countDeltaText }}</span>
          </span>
        </div>
        <div class="health-metric">
          <span class="health-metric-name">Critical reviews, last 30 days</span>
          <span class="health-metric-value" :class="{ critical: flagged('new_critical_reviews') }">{{ critical30dText }}</span>
        </div>
        <div v-if="sparkline" class="review-health-trend">
          <div class="review-health-trend-label">
            <span>Rating count, last {{ sparkline.days }} days</span>
            <span>{{ sparkline.firstText }} → {{ sparkline.lastText }}</span>
          </div>
          <svg class="review-health-sparkline" viewBox="0 0 200 40" preserveAspectRatio="none" role="img" :aria-label="'Rating count trend: ' + sparkline.firstText + ' to ' + sparkline.lastText">
            <polyline :points="sparkline.points" fill="none" stroke="currentColor" stroke-width="2" vector-effect="non-scaling-stroke" stroke-linejoin="round" stroke-linecap="round"></polyline>
          </svg>
        </div>
      </div>
    </template>

    <div class="review-health-footer">
      <span v-if="asOfText" class="review-health-asof">Amazon data as of {{ asOfText }}</span>
      <a
        v-if="reviewsUrl"
        :href="reviewsUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="review-health-link"
      >Read reviews on Amazon</a>
    </div>
  </div>
</template>

<script>
import {
  numOrNull,
  formatRating,
  formatCount,
  formatSnapshotDate,
  formatSignedDelta
} from '../../utils/reviewHealth.js'

/** GET /api/v1/products/:id/review_health body. state "hidden" (or no body) renders nothing. */
export default {
  name: 'ReviewHealth',
  props: {
    reviewHealth: {
      type: Object,
      default: null
    }
  },
  computed: {
    state: function () {
      return (this.reviewHealth && this.reviewHealth.state) || 'hidden'
    },
    visible: function () {
      return this.state === 'ready' || this.state === 'waiting'
    },
    waiting: function () {
      return this.state === 'waiting' || !this.latest
    },
    latest: function () {
      return (this.reviewHealth && this.reviewHealth.latest) || null
    },
    previous: function () {
      return (this.reviewHealth && this.reviewHealth.previous) || null
    },
    watchdog: function () {
      return (this.reviewHealth && this.reviewHealth.watchdog) || null
    },
    status: function () {
      return this.watchdog && this.watchdog.status ? String(this.watchdog.status).toLowerCase() : ''
    },
    ratingText: function () {
      return formatRating(this.latest && this.latest.display_rating)
    },
    previousRatingText: function () {
      var p = this.previous
      if (!p || numOrNull(p.display_rating) == null) return ''
      var date = formatSnapshotDate(p.snapshot_date)
      return 'was ' + formatRating(p.display_rating) + (date ? ' on ' + date : '')
    },
    countText: function () {
      return formatCount(this.latest && this.latest.total_rating_count)
    },
    countDeltaText: function () {
      var p = this.previous
      if (!p || !this.latest) return ''
      var delta = formatSignedDelta(this.latest.total_rating_count, p.total_rating_count)
      if (delta == null) return ''
      var date = formatSnapshotDate(p.snapshot_date)
      return delta + (date ? ' since ' + date : '')
    },
    critical30dText: function () {
      return formatCount(this.reviewHealth && this.reviewHealth.critical_reviews_30d)
    },
    asOfText: function () {
      return this.latest ? formatSnapshotDate(this.latest.snapshot_date, true) : ''
    },
    reviewsUrl: function () {
      return (this.reviewHealth && this.reviewHealth.reviews_url) || null
    },
    /** Inline polyline of history[].total_rating_count (oldest first); needs 2+ known points. */
    sparkline: function () {
      var history = (this.reviewHealth && Array.isArray(this.reviewHealth.history)) ? this.reviewHealth.history : []
      var pts = []
      history.forEach(function (h) {
        var n = numOrNull(h && h.total_rating_count)
        if (n != null) pts.push(n)
      })
      if (pts.length < 2) return null
      var min = Math.min.apply(null, pts)
      var max = Math.max.apply(null, pts)
      var span = max - min
      var step = 200 / (pts.length - 1)
      var points = pts.map(function (v, i) {
        var y = span === 0 ? 20 : 36 - ((v - min) / span) * 32
        return (i * step).toFixed(1) + ',' + y.toFixed(1)
      }).join(' ')
      return {
        points: points,
        days: history.length,
        firstText: formatCount(pts[0]),
        lastText: formatCount(pts[pts.length - 1])
      }
    }
  },
  methods: {
    /** Reason is part of the current red/amber alert. */
    flagged: function (reason) {
      if (this.status !== 'red' && this.status !== 'amber') return false
      var reasons = (this.watchdog && this.watchdog.reasons) || []
      return reasons.indexOf(reason) >= 0
    }
  }
}
</script>

<style scoped>
.review-health-card {
  display: flex;
  flex-direction: column;
}

.review-health-metrics {
  padding-top: 8px;
  gap: 0;
}

.review-health-waiting {
  padding: 24px;
  font-size: 13px;
  color: #6b7280;
}

.review-health-trend {
  padding: 12px 0 0;
  color: #6b7280;
}

.review-health-trend-label {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  margin-bottom: 6px;
}

.review-health-sparkline {
  display: block;
  width: 100%;
  height: 40px;
  color: #3b82f6;
}

.review-health-footer {
  margin-top: auto;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-top: 1px solid #e5e7eb;
  font-size: 12px;
  color: #9ca3af;
}

.review-health-link {
  font-weight: 600;
  color: var(--accent-primary, #2563eb);
  text-decoration: none;
}

.review-health-link:hover {
  text-decoration: underline;
}
</style>



// WEBPACK FOOTER //
// ReviewHealth.vue?28fccdc4